import { WebsiteProvider } from './context/WebsiteContext';
import AdminWebsite from './pages/admin/AdminWebsite';
import ShopPage from './pages/ShopPage';
import React from 'react';
import StaffPortalBanner from './components/StaffPortalBanner';
import AdminContacts from './pages/admin/AdminContacts';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';

// Layout Components
import Header from './components/Header';
import Footer from './components/Footer';
import ChatWidget from './components/ChatWidget';

// Public Pages
import HomePage from './pages/HomePage';
import BackToSchoolPage from './pages/BackToSchoolPage';
import SchoolProductsPage from './pages/SchoolProductsPage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import CartPage from './pages/CartPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CheckoutPage from './pages/CheckoutPage';

// Portal Pages
import PortalLoginPage from './pages/PortalLoginPage';
import PortalDashboard from './pages/PortalDashboard';
import SetPasswordPage from './pages/SetPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DesignBuilderPage from './pages/DesignBuilderPage';
import OrderConfigPage from './pages/OrderConfigPage';
import ReorderPage from './pages/ReorderPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import QuoteBuilderPage from './pages/QuoteBuilderPage';
import QuoteSuccessPage from './pages/QuoteSuccessPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminProductImport from './pages/admin/AdminProductImport';
import AdminOrders from './pages/admin/AdminOrders';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminOrderSync from './pages/admin/AdminOrderSync';
import AdminAnnouncements from './pages/admin/AdminAnnouncements';
import AdminCategories from './pages/admin/AdminCategories';
import AdminSettings from './pages/admin/AdminSettings';
import AdminSmartSite from './pages/admin/AdminSmartSite';
import AdminInStoreQuote from './pages/admin/AdminInStoreQuote';
import AdminQuotes from './pages/admin/AdminQuotes';
import AdminMockupTemplates from './pages/admin/AdminMockupTemplates';
import AdminDesignReviews from './pages/admin/AdminDesignReviews';
import AdminUsers from './pages/admin/AdminUsers';
import AdminProductForm from './pages/admin/AdminProductForm';
import AdminPlacements from './pages/admin/AdminPlacements';
import AdminSizingCharts from './pages/admin/AdminSizingCharts';

import { SmartSiteProvider } from './context/SmartSiteContext';

// Layout wrapper for public pages
const PublicLayout = ({ children }) => (
  <>
    <Header />
    <StaffPortalBanner />
    <main className="min-h-screen">{children}</main>
    <Footer />
  </>
);

function App() {
  return (
    <AuthProvider>
      <CustomerAuthProvider>
        <CartProvider>
          <Router>
            <WebsiteProvider><SmartSiteProvider>
            <ChatWidget />
            <Routes>
              {/* Public Routes */}
              <Route path="/shop" element={<PublicLayout><ShopPage /></PublicLayout>} />
              <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
              <Route path="/back-to-school" element={<PublicLayout><BackToSchoolPage /></PublicLayout>} />
              <Route path="/school/:slug" element={<PublicLayout><SchoolProductsPage /></PublicLayout>} />
              <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
              <Route path="/services/:slug" element={<PublicLayout><ServiceDetailPage /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
              <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
              <Route path="/quote" element={<Navigate to="/quote/builder" replace />} />
              <Route path="/cart" element={<PublicLayout><CartPage /></PublicLayout>} />
              <Route path="/product/:slug" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
              <Route path="/checkout" element={<CheckoutPage />} />

              {/* Client Portal Routes — wrapped with PublicLayout so users can still browse and shop */}
              <Route path="/portal/login" element={<PublicLayout><PortalLoginPage /></PublicLayout>} />
              <Route path="/portal" element={<PublicLayout><PortalDashboard /></PublicLayout>} />
              <Route path="/portal/dashboard" element={<PublicLayout><PortalDashboard /></PublicLayout>} />
              <Route path="/portal/set-password" element={<PublicLayout><SetPasswordPage /></PublicLayout>} />
              <Route path="/portal/reset-password" element={<PublicLayout><ResetPasswordPage /></PublicLayout>} />
              <Route path="/portal/orders/:orderId/design" element={<PublicLayout><DesignBuilderPage /></PublicLayout>} />
              <Route path="/portal/orders/:orderId/configure" element={<PublicLayout><OrderConfigPage /></PublicLayout>} />
              <Route path="/portal/orders/:orderId/reorder" element={<PublicLayout><ReorderPage /></PublicLayout>} />
              <Route path="/portal/payment-success" element={<PublicLayout><PaymentSuccessPage /></PublicLayout>} />
              
              {/* Quote Builder (public) */}
              <Route path="/quote/builder" element={<QuoteBuilderPage />} />
              <Route path="/quote/success" element={<QuoteSuccessPage />} />

              {/* Admin Routes */}
              <Route path="/admin/contacts" element={<AdminContacts />} />
              <Route path="/admin/contacts/:id" element={<AdminContacts />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/products/import" element={<AdminProductImport />} />
              <Route path="/admin/products/:id/preview" element={<PublicLayout><ProductDetailPage preview /></PublicLayout>} />
              <Route path="/admin/products/new" element={<AdminProductForm />} />
              <Route path="/admin/products/:id/edit" element={<AdminProductForm />} />
              <Route path="/admin/placements" element={<AdminPlacements />} />
              <Route path="/admin/sizing-charts" element={<AdminSizingCharts />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/order-sync" element={<AdminOrderSync />} />
              <Route path="/admin/orders/:id" element={<AdminOrderDetail />} />
              <Route path="/admin/announcements" element={<AdminAnnouncements />} />
              <Route path="/admin/mockup-templates" element={<AdminMockupTemplates />} />
              <Route path="/admin/design-reviews" element={<AdminDesignReviews />} />
              <Route path="/admin/categories" element={<AdminCategories />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/smart-site" element={<Navigate to="/admin/settings/smart-site" replace />} />
              <Route path="/admin/website" element={<AdminWebsite />} />
              <Route path="/admin/settings/smart-site" element={<AdminSmartSite />} />
              <Route path="/admin/in-store-quote" element={<AdminInStoreQuote />} />
              <Route path="/admin/quotes" element={<AdminQuotes />} />
              <Route path="/admin/users" element={<Navigate to="/admin/settings/users" replace />} />
              <Route path="/admin/settings/users" element={<AdminUsers />} />
            </Routes>
            </SmartSiteProvider></WebsiteProvider>
          </Router>
        </CartProvider>
      </CustomerAuthProvider>
    </AuthProvider>
  );
}

export default App;
